import { useCallback, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { CompareTab, ObjectId, UA_INSIGHTS_DIALOG_TYPES, UAInsightsDialogType, UAInsightsView } from '../types'

// Re-exported so existing consumers importing these navigation types from this
// hook (and from `useUAInsightsNavigation`) keep working; the canonical
// definitions live in `types/navigation.ts`.
export type { CompareTab, UAInsightsView } from '../types'

export const NETWORK_QUERY_PARAM = 'network'
export const MONTH_QUERY_PARAM = 'month'
export const GENRE_FILTER_QUERY_PARAM = 'genreFilter'
export const COMPARE_TAB_QUERY_PARAM = 'compareTab'

/**
 * Base path for the UA Insights dashboard. The dashboard's own sub-views
 * (overview vs Motivation vs Story Pattern detail) are expressed as real URL
 * paths beneath this base rather than query params, so they are shareable,
 * bookmarkable, and survive opening/closing the summary-card dialogs.
 */
const UA_INSIGHTS_BASE_PATH = '/ua-insights'

/**
 * Query-string keys owned by the dialog stack. `closeDialog` strips exactly
 * these so unrelated page state — the game page's `tab`, `network`/`month`
 * filters, future filters, etc. — survives when the user closes a dialog.
 */
const DIALOG_QUERY_PARAMS: readonly string[] = ['dialog', 'tagId', 'creativeId', 'gameId', 'genreId', 'motivationId', 'dimensionId', '_dd']

const COMPARE_TAB_VALUES: ReadonlyArray<CompareTab> = ['overview', 'motivations', 'storyPatterns', 'creativeTags']
const isCompareTab = (value: string | null): value is CompareTab => value !== null && (COMPARE_TAB_VALUES as ReadonlyArray<string>).includes(value)

const isDialogType = (value: string | null): value is UAInsightsDialogType =>
  value !== null && (UA_INSIGHTS_DIALOG_TYPES as ReadonlyArray<string>).includes(value)

/**
 * Every id in the URL is an opaque API id. They are kept as strings and passed
 * straight back to the API; an unknown id surfaces as a 404 from the endpoint
 * that receives it.
 */
interface DialogParams {
  dialog: UAInsightsDialogType | null
  tagId: ObjectId | null
  creativeId: ObjectId | null
  gameId: ObjectId | null
  /**
   * Identifies the *target* game for a comparison view. Kept separate from
   * `gameId` so the comparison can coexist with the listing's own dialog
   * params without either overwriting the other on subsequent navigations.
   */
  compareGameId: ObjectId | null
  /**
   * Identifies the *target* genre for a game-vs-genre comparison view. Held
   * separately from `compareGameId` so the URL unambiguously distinguishes a
   * game-vs-game comparison from a game-vs-genre one.
   */
  compareGenreId: ObjectId | null
  /**
   * Active tab on the Compare page. Persisted in the URL so deep links and
   * back/forward navigation restore the user's last view, and an
   * unrecognised/missing value falls back to the default `overview` tab
   * downstream.
   */
  compareTab: CompareTab | null
  genreId: ObjectId | null
  motivationId: ObjectId | null
  dimensionId: ObjectId | null
  networkId: ObjectId | null
  month: string | null
  /**
   * Dashboard-wide genre filter (genre id) applied alongside network and
   * month. Held under a dedicated `genreFilter` key so it doesn't collide
   * with the dialog-scoped `genreId` (which is stripped on dialog close);
   * `genreFilter` is intentionally persistent so navigating into and out of
   * dialogs preserves the user's dashboard scope.
   */
  genreFilter: ObjectId | null
  /** Active dashboard drill-down view (overview when null). */
  view: UAInsightsView | null
  /** Motivation id for the Motivation detail view. */
  viewMotivationId: ObjectId | null
  /** Pattern or sub-pattern id for the Story Pattern detail view. */
  viewPatternId: ObjectId | null
  /** Optional motivation lens (motivation id) applied to the Story Pattern detail view. */
  viewLensId: ObjectId | null
}

export const useUAInsightsDialogParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const routeParams = useParams()

  // The active drill-down view is derived from the URL path:
  //   /ua-insights                                          -> overview
  //   /ua-insights/motivation/:motivationId                 -> motivation view
  //   /ua-insights/pattern/:patternId                       -> pattern view
  //   /ua-insights/motivation/:motivationId/pattern/:patternId -> pattern lensed by a motivation
  const routeMotivation = routeParams.motivationId ?? null
  const routePattern = routeParams.patternId ?? null
  // The comparison target lives in the URL path (the Compare page routes
  // `/ua-insights/game/:gameId/compare/game/:targetGameId` and
  // `/.../compare/genre/:targetGenreId`), surfaced through the same
  // `compareGameId` / `compareGenreId` params the tab bodies and the
  // Shared/Diverging motivation drilldowns read.
  const routeTargetGameId = routeParams.targetGameId ?? null
  const routeTargetGenreId = routeParams.targetGenreId ?? null
  const params: DialogParams = useMemo(
    () => ({
      // Validated rather than cast: an unrecognised `?dialog=` value resolves
      // to `null` (no dialog) instead of a bogus type that every dialog then
      // silently fails to match.
      dialog: isDialogType(searchParams.get('dialog')) ? (searchParams.get('dialog') as UAInsightsDialogType) : null,
      tagId: searchParams.get('tagId'),
      creativeId: searchParams.get('creativeId'),
      gameId: searchParams.get('gameId'),
      compareGameId: routeTargetGameId,
      compareGenreId: routeTargetGenreId,
      compareTab: isCompareTab(searchParams.get(COMPARE_TAB_QUERY_PARAM)) ? (searchParams.get(COMPARE_TAB_QUERY_PARAM) as CompareTab) : null,
      genreId: searchParams.get('genreId'),
      motivationId: searchParams.get('motivationId'),
      dimensionId: searchParams.get('dimensionId'),
      networkId: searchParams.get(NETWORK_QUERY_PARAM),
      month: searchParams.get(MONTH_QUERY_PARAM),
      genreFilter: searchParams.get(GENRE_FILTER_QUERY_PARAM),
      // When a pattern segment is present the route is the pattern view; an
      // accompanying motivation segment then acts as the active lens rather than
      // a standalone motivation view.
      view: routePattern ? 'pattern' : routeMotivation ? 'motivation' : null,
      viewMotivationId: routePattern ? null : routeMotivation,
      viewPatternId: routePattern,
      viewLensId: routePattern ? routeMotivation : null,
    }),
    [searchParams, routeMotivation, routePattern, routeTargetGameId, routeTargetGenreId]
  )

  // Always show the back arrow whenever a dialog is open; clicking it always
  // performs a browser-history back navigation via `goBack`.
  const canGoBack = !!params.dialog

  const openDialog = useCallback(
    (dialog: UAInsightsDialogType, extra?: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('_dd')
      newParams.set('dialog', dialog)
      if (extra) {
        Object.entries(extra).forEach(([key, value]) => newParams.set(key, value))
      }
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  const closeDialog = useCallback(() => {
    // Strip only the dialog-specific keys so unrelated page state (the game
    // page's `tab`, the dashboard's `network`/`month` filters, future
    // filters, etc.) persists across dialog open/close cycles.
    const next = new URLSearchParams(searchParams)
    DIALOG_QUERY_PARAMS.forEach((key) => next.delete(key))
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const setNetwork = useCallback(
    (networkId: ObjectId | null) => {
      const newParams = new URLSearchParams(searchParams)
      if (networkId) newParams.set(NETWORK_QUERY_PARAM, networkId)
      else newParams.delete(NETWORK_QUERY_PARAM)
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  const setMonth = useCallback(
    (month: string | null) => {
      const newParams = new URLSearchParams(searchParams)
      if (month) newParams.set(MONTH_QUERY_PARAM, month)
      else newParams.delete(MONTH_QUERY_PARAM)
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  const setGenreFilter = useCallback(
    (genreId: ObjectId | null) => {
      const newParams = new URLSearchParams(searchParams)
      if (genreId) newParams.set(GENRE_FILTER_QUERY_PARAM, genreId)
      else newParams.delete(GENRE_FILTER_QUERY_PARAM)
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Persist the active compare-result tab in the URL via `replace` so tab
  // toggles don't pile up in browser history — back/forward should still step
  // through dialog open/close events, not every time the user flips between
  // Motivations and Creative Tags.
  const setCompareTab = useCallback(
    (tab: CompareTab | null) => {
      const newParams = new URLSearchParams(searchParams)
      if (tab) newParams.set(COMPARE_TAB_QUERY_PARAM, tab)
      else newParams.delete(COMPARE_TAB_QUERY_PARAM)
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const goBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // Preserve the dashboard-scope filters (network / month / genre) across view
  // transitions while dropping any dialog-specific query state, so navigating
  // between views starts from a clean dialog stack but keeps the user's scope.
  const filterSearch = useCallback(() => {
    const next = new URLSearchParams()
    const network = searchParams.get(NETWORK_QUERY_PARAM)
    const month = searchParams.get(MONTH_QUERY_PARAM)
    const genreFilter = searchParams.get(GENRE_FILTER_QUERY_PARAM)
    if (network) next.set(NETWORK_QUERY_PARAM, network)
    if (month) next.set(MONTH_QUERY_PARAM, month)
    if (genreFilter) next.set(GENRE_FILTER_QUERY_PARAM, genreFilter)
    const qs = next.toString()
    return qs ? `?${qs}` : ''
  }, [searchParams])

  const openMotivationView = useCallback(
    (motivationId: ObjectId) => {
      navigate(`${UA_INSIGHTS_BASE_PATH}/motivation/${motivationId}${filterSearch()}`)
    },
    [navigate, filterSearch]
  )

  // Open the Story Pattern detail view, optionally lensed through a motivation
  // (the path the user arrived from). The lens, when present, is encoded as a
  // /motivation/:lensId prefix segment ahead of /pattern/:patternId.
  const openPatternView = useCallback(
    (patternId: ObjectId, lensMotivationId?: ObjectId | null) => {
      const lensSegment = lensMotivationId ? `/motivation/${lensMotivationId}` : ''
      navigate(`${UA_INSIGHTS_BASE_PATH}${lensSegment}/pattern/${patternId}${filterSearch()}`)
    },
    [navigate, filterSearch]
  )

  const backToOverview = useCallback(() => {
    navigate(`${UA_INSIGHTS_BASE_PATH}${filterSearch()}`)
  }, [navigate, filterSearch])

  return {
    params,
    openDialog,
    closeDialog,
    canGoBack,
    goBack,
    setNetwork,
    setMonth,
    setGenreFilter,
    setCompareTab,
    openMotivationView,
    openPatternView,
    backToOverview,
  }
}
